/**
 * EventBus — Simple pub/sub for loose coupling
 * Per PLAN.md core/EventBus.js
 */
export class EventBus {
    constructor() {
        this.listeners = new Map();
    }

    /**
     * Subscribe to an event
     * @param {string} event 
     * @param {Function} callback 
     * @returns {Function} Unsubscribe function
     */
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);

        return () => this.off(event, callback);
    }

    /**
     * Unsubscribe from an event
     * @param {string} event 
     * @param {Function} callback 
     */
    off(event, callback) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.delete(callback);
        }
    }

    /**
     * Emit an event with data
     * @param {string} event 
     * @param {*} data 
     */
    emit(event, data) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(cb => cb(data));
        }
    }

    /**
     * Subscribe once
     * @param {string} event 
     * @param {Function} callback 
     */
    once(event, callback) {
        const unsubscribe = this.on(event, (data) => {
            unsubscribe();
            callback(data);
        });
    }
}

// Global event bus instance
export const eventBus = new EventBus();

// Event types
export const Events = {
    // Canvas events
    CANVAS_PAN: 'canvas:pan',
    CANVAS_ZOOM: 'canvas:zoom',
    CANVAS_CLICK: 'canvas:click',
    CANVAS_DBLCLICK: 'canvas:dblclick',

    // Node events
    NODE_CREATE_REQUEST: 'node:create-request',  // Request to create a node
    NODE_CREATE: 'node:create',  // Node was created (notification)
    NODE_UPDATE: 'node:update',
    NODE_DELETE: 'node:delete',
    NODE_SELECT: 'node:select',
    NODE_DESELECT: 'node:deselect',
    NODE_MOVE: 'node:move',

    // Connection events
    CONNECTION_CREATE: 'connection:create',
    CONNECTION_DELETE: 'connection:delete',

    // Group events
    GROUP_CREATE: 'group:create',
    GROUP_DELETE: 'group:delete',

    // Tool events
    TOOL_CHANGE: 'tool:change',

    // Editor events
    EDITOR_OPEN: 'editor:open',
    EDITOR_CLOSE: 'editor:close',

    // Document events
    DOCUMENT_SAVE: 'document:save',
    DOCUMENT_LOAD: 'document:load',

    // Theme events
    THEME_CHANGE: 'theme:change',

    // Grid events
    GRID_TOGGLE: 'grid:toggle',

    // History events
    HISTORY_UNDO: 'history:undo',
    HISTORY_REDO: 'history:redo',
    HISTORY_PUSH: 'history:push',

    // Render request
    RENDER_REQUEST: 'render:request'
};
